///
/// EarthBrowser
///
/// Created by Matt Giger on 11/20/2023
/// Copyright (c) 2023 Geodata Labs
///

import { type Camera } from "@stovokor/camera/Camera"
import { type frustum } from "@stovokor/math/frustum"
import { matrix } from "@stovokor/math/matrix"
import { ray } from "@stovokor/math/ray"
import { type rect } from "@stovokor/math/rect"
import { type sphere } from "@stovokor/math/sphere"
import { time } from "@stovokor/math/time"
import { vec3 } from "@stovokor/math/vector"

/**
 * Per-node memo for the matrices pushTransform derives. Matrices are
 * immutable objects, so identity comparisons on the inputs (the node's
 * local transform, the parent's model matrix, the camera's view and
 * projection) tell whether last frame's products are still valid; a static
 * node under a static camera then costs no multiplies or allocations per
 * frame. Also memoizes the view-space bounding sphere used for culling.
 */
export class NodeMatrixCache {
	public local?: matrix
	public parent?: matrix
	public model?: matrix
	public view?: matrix
	public projection?: matrix
	public modelView?: matrix
	public modelViewProjection?: matrix
	public sphereSource?: sphere
	public sphereModelView?: matrix
	public viewSphere?: sphere
}

// Ray used while no pointer ray has been pushed; shared, never mutated
const cDefaultRay = new ray()

export class RenderState {
	public static lightDirection = new vec3(0, 0, 1)	// such a hack

	public camera!: Camera
	public ray: ray = cDefaultRay
	public viewMatrix!: matrix
	public modelMatrix = matrix.identity
	public modelView!: matrix
	public projection!: matrix
	public modelViewProjection!: matrix
	public frustum!: frustum
	public realTime!: time
	public width!: number
	public height!: number
	// Device pixels per logical (CSS) pixel of the target drawing buffer.
	// width/height and screen projections are logical; multiply by this for
	// anything specified in device pixels (gl_PointSize, scissor rects)
	public pixelRatio: number = 1
	public mouseX?: number
	public mouseY?: number
	public clipRect?: rect

	private rayStack: ray[] = []
	private modelMatrixStack: matrix[] = []
	private modelViewStack: matrix[] = []
	private modelViewProjectionStack: matrix[] = []

	/**
	 * @param realTime - Wall-clock time for this state; the viewports pass
	 * the frame's shared `context.frameTime` so the three passes agree and
	 * don't each allocate a Date
	 */
	constructor(camera: Camera, mouseX?: number, mouseY?: number, realTime?: time) {
		this.beginFrame(camera, realTime ?? new time())
		this.mouseX = mouseX
		this.mouseY = mouseY
		if(mouseX != undefined && mouseY != undefined) {
			this.ray = camera.rayFrom(mouseX, mouseY)
		}
	}

	/**
	 * Reinitialize for a new frame from the camera's current matrices. The
	 * viewports keep one RenderState each and call this per frame instead
	 * of constructing a new one (three stacks and a frustum per viewport)
	 */
	public beginFrame(camera: Camera, realTime: time): void {
		this.camera = camera
		this.width = camera.projection.width
		this.height = camera.projection.height
		this.mouseX = undefined
		this.mouseY = undefined
		this.ray = cDefaultRay
		this.clipRect = undefined
		this.realTime = realTime

		this.viewMatrix = camera.viewMatrix
		this.modelMatrix = matrix.identity
		this.modelView = this.viewMatrix
		this.projection = camera.projectionMatrix
		this.modelViewProjection = camera.modelViewProjection
		this.frustum = camera.frustum

		this.rayStack.length = 0
		this.modelMatrixStack.length = 0
		this.modelViewStack.length = 0
		this.modelViewProjectionStack.length = 0
	}

	/**
	 * Depth of the current model origin in view space: its distance in
	 * front of the camera along the view axis (negative behind it). The
	 * viewports sort draws by this; it is not the world z coordinate
	 */
	public get viewDepth(): number {
		return -this.modelView.positionZ
	}

	/** View-space depth of a point given in the current model space */
	public viewDepthOf(point: vec3): number {
		const m = this.modelView.glArrayAccess()
		return -(m[2] * point.x + m[6] * point.y + m[10] * point.z + m[14])
	}

	/**
	 * Enter a node's coordinate space. With a cache, the products are
	 * reused while the local transform, parent model matrix and camera
	 * matrices are the same objects as last time. Returns the model matrix.
	 */
	public pushTransform(transform: matrix, cache?: NodeMatrixCache): matrix {
		const parent = this.modelMatrix
		this.modelMatrixStack.push(parent)
		this.modelViewStack.push(this.modelView)
		this.modelViewProjectionStack.push(this.modelViewProjection)

		let model: matrix
		if(cache !== undefined && cache.local === transform && cache.parent === parent && cache.model !== undefined) {
			model = cache.model
		} else {
			model = transform.multiply(parent)
			if(cache !== undefined) {
				cache.local = transform
				cache.parent = parent
				cache.model = model
				cache.view = undefined
			}
		}

		let modelView: matrix
		let modelViewProjection: matrix
		if(cache !== undefined && cache.view === this.viewMatrix && cache.projection === this.projection
			&& cache.modelView !== undefined && cache.modelViewProjection !== undefined) {
			modelView = cache.modelView
			modelViewProjection = cache.modelViewProjection
		} else if(cache !== undefined) {
			// The view products change every frame the camera moves, for
			// every visible node: recompute into the matrices the cache
			// already owns rather than allocating two per node per frame.
			// Nothing holds them across frames (renderers re-read them each
			// stateUpdate), but the view-sphere memo keys on the modelView
			// identity, which an in-place update keeps, so drop it here
			if(cache.modelView !== undefined && cache.modelViewProjection !== undefined) {
				modelView = model.multiplyInto(this.viewMatrix, cache.modelView)
				modelViewProjection = modelView.multiplyInto(this.projection, cache.modelViewProjection)
			} else {
				modelView = model.multiply(this.viewMatrix)
				modelViewProjection = modelView.multiply(this.projection)
				cache.modelView = modelView
				cache.modelViewProjection = modelViewProjection
			}
			cache.view = this.viewMatrix
			cache.projection = this.projection
			cache.viewSphere = undefined
		} else {
			modelView = model.multiply(this.viewMatrix)
			modelViewProjection = modelView.multiply(this.projection)
		}

		this.modelMatrix = model
		this.modelView = modelView
		this.modelViewProjection = modelViewProjection
		return model
	}

	/**
	 * The model matrix a node with this local transform would have under
	 * the current parent, without entering its space or deriving the view
	 * products. For leaves that only need their world placement (a marker
	 * feeding an instance buffer): memoized like pushTransform, so a static
	 * node costs nothing per frame
	 */
	public modelOf(transform: matrix, cache: NodeMatrixCache): matrix {
		const parent = this.modelMatrix
		if(cache.local === transform && cache.parent === parent && cache.model !== undefined) {
			return cache.model
		}
		const model = transform.multiply(parent)
		cache.local = transform
		cache.parent = parent
		cache.model = model
		cache.view = undefined
		return model
	}

	public popTransform(): void {
		this.modelMatrix = this.modelMatrixStack.pop() ?? matrix.identity
		this.modelView = this.modelViewStack.pop() ?? this.viewMatrix
		this.modelViewProjection = this.modelViewProjectionStack.pop() ?? this.camera.modelViewProjection
	}

	/**
	 * Bounding sphere in view space for frustum culling, memoized per node
	 * on the sphere and modelView identities
	 */
	public viewSphere(source: sphere, cache: NodeMatrixCache): sphere {
		if(cache.viewSphere === undefined || cache.sphereSource !== source || cache.sphereModelView !== this.modelView) {
			cache.viewSphere = source.transform(this.modelView)
			cache.sphereSource = source
			cache.sphereModelView = this.modelView
		}
		return cache.viewSphere
	}

	public pushRay(ray: ray): void {
		this.rayStack.push(this.ray)
		this.ray = ray
	}

	public popRay(): void {
		this.ray = this.rayStack.pop() ?? cDefaultRay
	}
}
